/* @flow */
import { chatStreams } from './resolver/chatStreams';
import { organizations } from './resolver/organizations';
import { reactionSummary } from './resolver/reactionSummary';
import { cachedChatStreams } from './resolver/cachedChatStreams';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: {
    __schema: {
      types: [
        {
          kind: 'INTERFACE',
          name: 'NavigationSectionItem',
          possibleTypes: [{ name: 'Class' }, { name: 'Organization' }],
        },
      ],
    },
  },
});

export function dataIdFromObject(result: Object) {
  // DeliverySummaryRecipients are sometimes loaded twice into the apollo cache
  // because they can appear as "delivered" but also be missing parents, so will
  // appear in the "unidentified people" section. if we only cache by uuid, the
  // delivery error gets wiped in the second section.
  if (result.__typename === 'DeliverySummaryRecipient' && result.uuid && result.deliveryError) {
    return result.__typename + result.uuid + result.deliveryError.code;
  }

  // CoachingSessions get loaded via two separate queries (getPreviousSessions and
  // getUpcomingSessions). If a recurring CoachingSession has some instances in the past
  // and some instances in the future, the same `uuid` will appear in both queries, and
  // without this fix, the result from getPreviousSessions will override the result from
  // getUpcomingSessions because they end up with the same DataId.
  if (
    result.__typename === 'CoachingSession' &&
    result.uuid &&
    result.instances &&
    result.instances[0] &&
    result.instances.slice(-1)[0]
  ) {
    return (
      result.__typename +
      result.uuid +
      result.instances.length +
      result.instances[0].startDateTime +
      result.instances.slice(-1)[0].startDateTime
    );
  }

  if (
    result.__typename === 'ExpandedCoachingSessionInstance' &&
    result.coachingSessionUuid &&
    result.startDateTime
  ) {
    return result.__typename + result.coachingSessionUuid + result.startDateTime;
  }

  if (result.uuid) {
    return result.__typename + result.uuid;
  }

  if (result.id && result.__typename) {
    return result.__typename + result.id;
  }

  // LinkedAccount
  if (result.__typename && result.provider) {
    return result.__typename + result.provider;
  }

  if (result.__typename === 'SftpFeatureCredentials' && result.credentials.username) {
    return result.__typename + result.credentials.username;
  }

  if (result.__typename === 'LearningToolsInteroperabilityIntegration' && result.serviceKey) {
    return result.__typename + result.serviceKey;
  }

  if (result.__typename === 'AdminIntegration' && result.providerKey) {
    return result.__typename + result.providerKey;
  }

  if (result.__typename === 'PartnerIntegration' && result.providerKey) {
    return result.__typename + result.providerKey;
  }

  if (result.__typename === 'ContentSource' && result.providerKey) {
    return result.__typename + result.providerKey;
  }

  if (
    result.__typename === 'LearningToolsInteroperabilityIntegration' &&
    result.organizationUUID &&
    result.serviceKey
  ) {
    return result.__typename + result.serviceKey + result.organizationUUID;
  }

  if (result.__typename === 'ContentSourceProvider' && result.providerKey) {
    return result.__typename + result.providerKey;
  }

  // SequenceItem's key is the item uuid + sequence id.
  // So it's should be unique and not collide with message uuids.
  if (result.__typename === 'SequenceItem' && result.key) {
    return result.key;
  }

  if (result.__typename === 'ReactionSummary' && result.messageUuid) {
    return result.__typename + '_' + result.messageUuid;
  }

  if (result.__typename === 'LinkPreview' && result.url) {
    return result.__typename + '_' + result.url;
  }

  if (result.__typename === 'OrganizationMember' && result.user) {
    return result.__typename + '_' + result.user.uuid;
  }

  return null;
}

export function newInMemoryCache() {
  return new InMemoryCache({
    fragmentMatcher,
    dataIdFromObject,
    // NB: Returning null from a custom resolver will cause it not
    // to hit the network even though it can't find the item in the cache.
    cacheRedirects: {
      Query: {
        organizations,
        reactionSummary,
        cachedChatStreams,
        chatStreams,
      },
    },
  });
}
